import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { TextComponent } from 'design/text/textComponents';
import { RefundFormValues, RefundFormViolation } from 'types/refundTypes';
import { createRefundFormViolationLense } from 'utils/refundUtils';

type Props = {
  values: RefundFormValues;
  violations: RefundFormViolation[];
  disabled: boolean;
  onAmountChange(nextValue: string): void;
  onCommentChange(nextValue: string): void;
};

const AMOUNT_INPUT_MAX_LENGTH = 15;
const COMMENT_INPUT_MAX_LENGTH = 255;

export const RefundFieldset = (props: Props) => {
  const {
    disabled,
    values,
    violations,
    onAmountChange,
    onCommentChange,
  } = props;

  const {
    inputValidationRequired,
    inputValidationAmountExceedsBalance,
    refundForm: ownDictionary,
  } = useIntlDictionary();

  const amountHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'MissingAmount':
          return inputValidationRequired;
        case 'AmountExceedsBalance':
          return inputValidationAmountExceedsBalance;
      }
    }
  }, [
    inputValidationAmountExceedsBalance,
    inputValidationRequired,
    violations,
  ]);

  const amountIsInvalid = violations.some((violation) =>
    createRefundFormViolationLense(violation).isAmountViolation()
  );

  const commentHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'MissingComment':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const commentIsInvalid = violations.some((violation) =>
    createRefundFormViolationLense(violation).isCommentViolation()
  );

  return (
    <>
      <FloatingLabelComponent
        lifted={!!values.amount}
        invalid={amountIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.amountInputLabel}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {amountHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="amount"
          type="text"
          value={values.amount}
          disabled={disabled}
          invalid={amountIsInvalid}
          maxLength={AMOUNT_INPUT_MAX_LENGTH}
          onValueChange={onAmountChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.comment}
        invalid={commentIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.commentInputLabel}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {commentHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="comment"
          type="text"
          value={values.comment}
          disabled={disabled}
          invalid={commentIsInvalid}
          maxLength={COMMENT_INPUT_MAX_LENGTH}
          onValueChange={onCommentChange}
        />
      </FloatingLabelComponent>
    </>
  );
};
